import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Seo } from '@/lib/seo';
import { Phone, House, ArrowRight, Warning, WhatsApp } from '@/components/ui/Icons';
import { company } from '@/data/company';
import { Logo } from '@/components/ui/Logo';
import { services } from '@/data/services';

export default function NotFound() {
  return (
    <>
      <Seo
        title="Page Not Found"
        description="The page you are looking for could not be found. Browse our roofing, driveway, landscaping, damp proofing and spray foam removal services across London, Essex and Suffolk."
        path="/404"
        noindex
      />
      <section className="relative isolate overflow-hidden bg-navy-950 pt-16 lg:pt-20">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(184,145,70,0.18),transparent_55%),radial-gradient(circle_at_75%_80%,rgba(39,69,112,0.45),transparent_60%)]"
        />
        <div aria-hidden="true" className="absolute inset-x-0 top-0 -z-10 h-1 gold-divider" />

        <div className="container-page relative w-full py-20 sm:py-28">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto max-w-2xl text-center text-paper-0"
          >
            <div className="flex justify-center">
              <Logo decorative interactive className="h-16" />
            </div>

            <p className="mt-10 inline-flex items-center gap-2 rounded-full border border-gold-400/40 bg-navy-900/50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-gold-300 backdrop-blur-xs">
              <Warning className="h-3 w-3" />
              404 - Page not found
            </p>

            <h1 className="mt-6 font-display text-[clamp(3rem,7vw,5.5rem)] font-semibold leading-[1.05] tracking-tighter">
              We can’t find that page
            </h1>

            <p className="mx-auto mt-5 max-w-md text-lg text-paper-0/80">
              The page you’re after may have moved or never existed. Try the homepage,
              or get in touch and we’ll point you in the right direction.
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link to="/" className="btn-gold group">
                <House className="h-4 w-4" />
                Back to home
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a href={`tel:${company.phoneTel}`} className="btn-outline-gold">
                <Phone className="h-4 w-4" />
                {company.phone}
              </a>
              <a
                href={`https://wa.me/${company.whatsappTel}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-gold"
              >
                <WhatsApp className="h-4 w-4" />
                WhatsApp
              </a>
            </div>

            <p className="mt-12 text-sm text-paper-0/60">Or jump to a popular service:</p>
            <ul className="mt-3 flex flex-wrap items-center justify-center gap-2 text-sm">
              {services.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    className="rounded-full border border-paper-0/15 bg-navy-900/50 px-3 py-1 text-paper-0/85 transition hover:-translate-y-0.5 hover:border-gold-400/60 hover:bg-navy-800 hover:text-gold-300"
                  >
                    {s.navLabel}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>
    </>
  );
}
